import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "src/components/ui/Card";
import { TutorExtendedProfileData } from "src/interfaces/TutorProfile";
import { Briefcase, Edit } from "lucide-react";
import { Button } from "src/components/ui/Button";

interface TeachingExperienceSectionProps {
  profileData: TutorExtendedProfileData;
  onEditExperience?: () => void;
}

const TeachingExperienceSection: React.FC<TeachingExperienceSectionProps> = ({ profileData, onEditExperience }) => {
  const experience = profileData.experience || [];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-green-600" />
          <CardTitle>Teaching Experience</CardTitle>
        </div>
        {onEditExperience && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 rounded-full hover:bg-gray-100"
            onClick={onEditExperience}
            aria-label="Edit Teaching Experience"
          >
            <Edit className="h-4 w-4 text-gray-500" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {experience.length === 0 ? (
          <p className="text-gray-500">No teaching experience specified.</p>
        ) : (
          <div className="space-y-4">
            {experience.map((exp, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-200 rounded-2xl px-8 py-6 shadow-sm hover:shadow-md transition-shadow mb-4"
              >
                {/* Prominent Role (not bold, just larger and underlined) */}
                <div className="text-2xl text-black mb-2 tracking-tight border-b border-gray-200 pb-1">
                  {exp.role ? (exp.role.charAt(0).toUpperCase() + exp.role.slice(1)) : "Teaching"} Teaching
                </div>
                {/* Details grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2 text-base mt-2">
                  <div className="space-y-1">
                    {exp.organization && (
                      <div><span className="uppercase text-xs text-gray-500 font-medium">Organization</span><br /><span className="text-gray-900">{exp.organization}</span></div>
                    )}
                    {exp.end_year && (
                      <div><span className="uppercase text-xs text-gray-500 font-medium">End Year</span><br /><span className="text-gray-900">{exp.end_year}</span></div>
                    )}
                  </div>
                  <div className="space-y-1">
                    {exp.location && (
                      <div><span className="uppercase text-xs text-gray-500 font-medium">Location</span><br /><span className="text-gray-900">{exp.location}</span></div>
                    )}
                    {exp.start_year && (
                      <div><span className="uppercase text-xs text-gray-500 font-medium">Start Year</span><br /><span className="text-gray-900">{exp.start_year}</span></div>
                    )}
                  </div>
                </div>
                {/* Years row below grid for clarity */}
                {typeof exp.years === 'number' && exp.years > 0 && (
                  <div className="mt-4 text-gray-900 font-medium text-base">
                    Years: <span className="text-gray-900">{exp.years}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeachingExperienceSection; 